/**
 * Ergovia ürün sayfası: galeri, varyant seçimi, paket seçici, sepete ekleme ve mobil yapışkan çubuk.
 */

/**
 * @param {number} cents
 * @param {string} format - Shopify para formatı, ör. "{{amount_with_comma_separator}} TL"
 */
export function formatMoney(cents, format) {
  const value = cents / 100;
  const group = (/** @type {string} */ fixed, /** @type {string} */ thousands, /** @type {string} */ decimal) => {
    const [whole = '0', fraction] = fixed.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? grouped + decimal + fraction : grouped;
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(value.toFixed(0), ',', '.');
      case 'amount_with_comma_separator':
        return group(value.toFixed(2), '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(value.toFixed(0), '.', ',');
      case 'amount_with_apostrophe_separator':
        return group(value.toFixed(2), "'", '.');
      default:
        return group(value.toFixed(2), ',', '.');
    }
  });
}

/** @param {HTMLElement} root */
function initProduct(root) {
  if (root.dataset.ergoReady) return;
  root.dataset.ergoReady = 'true';

  const moneyFormat = root.dataset.moneyFormat || '{{amount}}';
  const threshold = Number(root.dataset.threshold) || 0;
  const money = (/** @type {number} */ cents) => formatMoney(cents, moneyFormat);
  const data = JSON.parse(root.querySelector('[data-product-json]')?.textContent || '{"variants":[]}');

  const form = /** @type {HTMLFormElement | null} */ (root.querySelector('[data-product-form]'));
  const variantInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-variant-input]'));
  const quantityInput = /** @type {HTMLInputElement | null} */ (root.querySelector('[data-quantity-input]'));
  const atc = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-atc]'));
  const sticky = /** @type {HTMLElement | null} */ (root.querySelector('[data-sticky]'));
  const errorEl = /** @type {HTMLElement | null} */ (root.querySelector('[data-form-error]'));
  if (!form || !variantInput || !quantityInput || !atc) return;

  /* ---------- Galeri ---------- */
  const track = /** @type {HTMLElement | null} */ (root.querySelector('[data-gallery-track]'));
  const slides = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-slide]')]);
  const thumbs = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-thumb]')]);

  const goToSlide = (/** @type {number} */ index, smooth = true) => {
    const slide = slides[index];
    if (!track || !slide) return;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
  };

  thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => goToSlide(index)));

  if (track && thumbs.length && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const index = slides.indexOf(/** @type {HTMLElement} */ (entry.target));
          thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
        }
      },
      { root: track, threshold: 0.6 }
    );
    slides.forEach((slide) => observer.observe(slide));
  }

  /* ---------- Zoom ---------- */
  const zoom = /** @type {HTMLDialogElement | null} */ (root.querySelector('[data-zoom]'));
  const zoomImg = /** @type {HTMLImageElement | null} */ (root.querySelector('[data-zoom-img]'));
  const zoomStage = /** @type {HTMLElement | null} */ (root.querySelector('[data-zoom-stage]'));

  if (zoom && zoomImg && zoomStage) {
    slides.forEach((slide) =>
      slide.addEventListener('click', () => {
        zoomImg.src = slide.dataset.zoomSrc || '';
        zoomStage.classList.remove('is-zoomed');
        zoom.showModal();
      })
    );
    zoomStage.addEventListener('click', (event) => {
      const zoomed = zoomStage.classList.toggle('is-zoomed');
      if (!zoomed) return;
      // Tıklanan noktaya doğru kaydır
      const rect = zoomStage.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      requestAnimationFrame(() => {
        zoomStage.scrollLeft = (zoomStage.scrollWidth - zoomStage.clientWidth) * x;
        zoomStage.scrollTop = (zoomStage.scrollHeight - zoomStage.clientHeight) * y;
      });
    });
    root.querySelector('[data-zoom-close]')?.addEventListener('click', () => zoom.close());
  }

  /* ---------- Varyant + paket ---------- */
  const optionInputs = /** @type {HTMLInputElement[]} */ ([...root.querySelectorAll('[data-option-index]')]);
  const bundleInputs = /** @type {HTMLInputElement[]} */ ([...root.querySelectorAll('[data-bundle] input')]);

  const currentVariant = () => data.variants.find((/** @type {any} */ v) => String(v.id) === variantInput.value);

  const selectedBundle = () => {
    const input = bundleInputs.find((i) => i.checked);
    return {
      qty: input ? Number(input.value) : 1,
      off: input ? Number(input.dataset.off) : 0,
    };
  };

  const setText = (/** @type {string} */ selector, /** @type {string} */ text, /** @type {ParentNode} */ scope = root) => {
    const el = scope.querySelector(selector);
    if (el) el.textContent = text;
  };

  const render = () => {
    const variant = currentVariant();
    if (!variant) return;
    const { qty, off } = selectedBundle();
    const total = variant.price * qty - off;

    // Ana fiyat satırı: seçilen paketin toplamı, liste fiyatı (adet × üstü çizili fiyat) ve aradaki tasarruf
    const listUnit = variant.compare > variant.price ? variant.compare : variant.price;
    const listTotal = listUnit * qty;
    const hasSaving = listTotal > total;
    setText('[data-price-now]', money(total));
    const was = /** @type {HTMLElement | null} */ (root.querySelector('[data-price-was]'));
    const save = /** @type {HTMLElement | null} */ (root.querySelector('[data-price-save]'));
    if (was) {
      was.hidden = !hasSaving;
      was.textContent = money(listTotal);
    }
    if (save) {
      save.hidden = !hasSaving;
      save.textContent = `${money(listTotal - total).replace(/[,.]00(?=\D|$)/, '')} tasarruf`;
    }
    setText('[data-price-qty]', qty > 1 ? `KDV dahil · ${qty} adet toplam` : 'KDV dahil');

    // Paket kartları
    bundleInputs.forEach((input) => {
      const card = input.closest('[data-bundle]');
      if (!card) return;
      const cardQty = Number(input.value);
      const cardOff = Number(input.dataset.off);
      const full = variant.price * cardQty;
      const cardTotal = full - cardOff;
      card.classList.toggle('is-selected', input.checked);
      setText('[data-bundle-total]', money(cardTotal), card);
      setText('[data-bundle-full]', money(full), card);
      setText('[data-bundle-each]', money(Math.round(cardTotal / cardQty)), card);
      setText('[data-bundle-pct]', `%${Math.round((cardOff * 100) / full)} indirim`, card);
      setText(
        '[data-bundle-ship]',
        threshold > 0 && cardTotal >= threshold ? 'Ücretsiz kargo' : 'Kargo ücreti ödeme adımında eklenir',
        card
      );
    });

    quantityInput.value = String(qty);
    atc.disabled = !variant.available;
    setText('[data-atc-label]', variant.available ? 'Sepete Ekle' : 'Tükendi');
    setText('[data-atc-total]', variant.available ? money(total) : '');
    setText('[data-sticky-total]', money(total));
    const stickyBtn = /** @type {HTMLButtonElement | null} */ (root.querySelector('[data-sticky-btn]'));
    if (stickyBtn) stickyBtn.disabled = !variant.available;
  };

  optionInputs.forEach((input) =>
    input.addEventListener('change', () => {
      const selected = [];
      for (const option of optionInputs) {
        if (option.checked) selected[Number(option.dataset.optionIndex)] = option.value;
      }
      const variant = data.variants.find((/** @type {any} */ v) =>
        v.options.every((/** @type {string} */ value, /** @type {number} */ i) => value === selected[i])
      );
      if (!variant) return;
      variantInput.value = String(variant.id);
      selected.forEach((value, i) => setText(`[data-option-label="${i}"]`, value));

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url);

      if (variant.media) {
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(variant.media));
        if (index >= 0) goToSlide(index);
      }
      render();
    })
  );

  bundleInputs.forEach((input) => input.addEventListener('change', render));
  render();

  /* ---------- Sepete ekle ---------- */
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (atc.disabled || atc.classList.contains('is-loading')) return;
    atc.classList.add('is-loading');
    if (errorEl) errorEl.hidden = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantInput.value), quantity: Number(quantityInput.value) }] }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Ürün sepete eklenemedi.');

      if (window.ErgoCart) {
        await window.ErgoCart.refresh();
      } else {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }
    } catch (error) {
      if (errorEl) {
        errorEl.textContent = error instanceof Error ? error.message : 'Ürün sepete eklenemedi.';
        errorEl.hidden = false;
      }
    } finally {
      atc.classList.remove('is-loading');
    }
  });

  /* ---------- Mobil yapışkan çubuk ---------- */
  // Ana buton ekranda değilken çubuk görünür. Buton aşağıdaysa çubuk müşteriyi önce paket seçimine götürür,
  // buton yukarıda kaldıysa (müşteri paketi görmüşse) doğrudan sepete ekler.
  if (sticky && 'IntersectionObserver' in window) {
    const submitBtn = /** @type {HTMLElement | null} */ (root.querySelector('[data-sticky-btn]'));
    const chooseBtn = /** @type {HTMLElement | null} */ (root.querySelector('[data-sticky-choose]'));
    const target = /** @type {HTMLElement} */ (root.querySelector('.ergo-bundles') || form);

    chooseBtn?.addEventListener('click', () => target.scrollIntoView({ behavior: 'smooth', block: 'center' }));

    // Hızlı kaydırmada gözlemci tetiklenmeyebildiği için durum kaydırma sırasında da hesaplanır
    let queued = false;
    const update = () => {
      queued = false;
      const rect = atc.getBoundingClientRect();
      const visible = rect.bottom > 0 && rect.top < window.innerHeight;
      const below = !visible && rect.top > 0;
      sticky.hidden = visible;
      if (submitBtn) submitBtn.hidden = below;
      if (chooseBtn) chooseBtn.hidden = !below;
      document.body.classList.toggle('ergo-has-sticky', !visible);
    };
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    new IntersectionObserver(schedule).observe(atc);
    schedule();
  }
}

/**
 * Kapıda ödeme uygulaması (EasySell) aşağı kaydırınca ekranın altına kendi butonunu sabitler.
 * Temanın alt çubukları onun altında kalmasın diye yüksekliği CSS değişkenine yazılır.
 */
function trackAppBar() {
  let queued = false;
  const update = () => {
    queued = false;
    const bar = document.getElementById('es-sticky');
    let height = 0;
    if (bar) {
      const rect = bar.getBoundingClientRect();
      const style = getComputedStyle(bar);
      const shown = style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0;
      if (shown && rect.height > 0 && rect.bottom >= window.innerHeight - 2 && rect.top < window.innerHeight) {
        height = Math.round(rect.height);
      }
    }
    document.documentElement.style.setProperty('--ergo-bottom-offset', height + 'px');
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  schedule();
}

trackAppBar();

const initAll = () => document.querySelectorAll('[data-ergo-product]').forEach((el) => initProduct(/** @type {HTMLElement} */ (el)));

initAll();
document.addEventListener('shopify:section:load', initAll);
